// A Site's structure as GET /api/sites/:id/structure returns it
// (routes/structure.js), and the questions the Structure view asks of it
// before a write. The server decides every write again; these are the
// previews §7g asks for ("the preview names the ancestors that will empty")
// and must read the same way it does (services/structure.js).

import { colourName, perKg, period } from '../pickingJobs/rates'

export interface StructureSite {
  id: string
  name: string
  grower_pin: string | null
  level1_label: string
  level2_label: string
  level3_label: string
  show_level2: boolean
  map_level: number
  archived_at: string | null
  // Where the Map view opens (083).
  map_centre?: { lat: number; lng: number } | null
  map_zoom?: number | null
}

export interface StructureNode {
  id: string
  site_id: string
  level: 1 | 2 | 3
  name: string
  parent_id: string | null
  sort_order: number
  exact_variety: string | null
  plant_count: number | null
  location_id: string | null
  picking_job_id: string | null
  predecessor_id: string | null
  // A level 1 block's own PIN; null inherits the Site's (076).
  grower_pin: string | null
  archived_at: string | null
  created_at: string
  updated_at: string
  active: boolean
  // Final rows only.
  picking_recorded?: boolean
  picked_kg?: number
}

export interface StructureJob {
  id: string
  name: string
  colour: string | null
  exact_variety: string
  active: boolean
  archived_at: string | null
}

// A live boundary (084): one polygon on a node at the Site's mapping level.
export interface StructureBoundary {
  id: string
  node_id: string
  level: number
  points: { lat: number; lng: number }[]
  area_m2: number
  created_by: string | null
  created_at: string
}

export interface Structure {
  site: StructureSite
  nodes: StructureNode[]
  jobs: StructureJob[]
  boundaries: StructureBoundary[]
  varieties: SiteVariety[]
}

// One entry of the Site's variety list (migration 085), with how many
// active rows and jobs carry its name now. The name never changes.
export interface SiteVariety {
  id: string
  site_id: string
  name: string
  created_by: string | null
  created_at: string
  archived_at: string | null
  archived_by: string | null
  rows: number
  jobs: number
}

// The names offered for new work: the live list, in its order.
export const liveVarieties = (list: SiteVariety[] | undefined) => (list || []).filter((v) => !v.archived_at).map((v) => v.name)

type Tree = Map<string, StructureNode>
const index = (nodes: StructureNode[] | Tree): Tree => (nodes instanceof Map ? nodes : new Map(nodes.map((n) => [n.id, n])))

export const isActiveRow = (n: Pick<StructureNode, 'level' | 'archived_at'>) => n.level === 3 && !n.archived_at

const order = (a: StructureNode, b: StructureNode) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, undefined, { numeric: true }) || a.created_at.localeCompare(b.created_at)

export function childrenOf(nodes: StructureNode[], parentId: string | null): StructureNode[] {
  return nodes.filter((n) => (n.parent_id || null) === parentId).sort(order)
}

// Names from level 1 down to the node, as the server writes them.
export function pathOf(nodes: StructureNode[] | Tree, node: StructureNode | undefined | null): string {
  const byId = index(nodes)
  const out: string[] = []
  for (let cur = node; cur; cur = cur.parent_id ? byId.get(cur.parent_id) : undefined) out.unshift(cur.name)
  return out.join(' / ')
}

export function parentPath(nodes: StructureNode[] | Tree, node: StructureNode): string {
  const byId = index(nodes)
  return node.parent_id ? pathOf(byId, byId.get(node.parent_id)) : ''
}

// A path as the interface reads it: with level 2 hidden ("Hide level 2 in
// the interface"), its names are left out. The data still holds them.
export function shownPath(nodes: StructureNode[] | Tree, node: StructureNode | undefined | null, hideL2: boolean): string {
  const byId = index(nodes)
  const out: string[] = []
  for (let cur = node; cur; cur = cur.parent_id ? byId.get(cur.parent_id) : undefined) if (!(hideL2 && cur.level === 2)) out.unshift(cur.name)
  return out.join(' / ')
}

// The grower PIN a node works under (§7g): a block's own, else the Site's.
// "A block can be given its own PIN, which flows down to everything inside
// it; a tunnel or row cannot."
export interface PinSource {
  pin: string | null
  // The node or Site the PIN comes from; null when nothing sets one.
  from: 'own' | 'block' | 'site' | null
  block: StructureNode | null
}

export function pinOf(nodes: StructureNode[] | Tree, site: Pick<StructureSite, 'grower_pin'>, node: StructureNode): PinSource {
  const byId = index(nodes)
  let block: StructureNode | undefined = node
  while (block && block.level > 1) block = block.parent_id ? byId.get(block.parent_id) : undefined
  if (block?.grower_pin) return { pin: block.grower_pin, from: block.id === node.id ? 'own' : 'block', block }
  return { pin: site.grower_pin || null, from: site.grower_pin ? 'site' : null, block: block || null }
}

export function isUnder(nodes: StructureNode[] | Tree, node: StructureNode, ancestorId: string): boolean {
  const byId = index(nodes)
  for (let p: StructureNode | undefined = node; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) if (p.id === ancestorId) return true
  return false
}

// "A container cannot remain active with no active descendant row."
export function activeContainerIds(nodes: StructureNode[]): Set<string> {
  const byId = index(nodes)
  const active = new Set<string>()
  for (const n of nodes) {
    if (!isActiveRow(n)) continue
    let p = n.parent_id ? byId.get(n.parent_id) : undefined
    while (p && !active.has(p.id)) {
      active.add(p.id)
      p = p.parent_id ? byId.get(p.parent_id) : undefined
    }
  }
  return active
}

export interface Emptied {
  id: string
  level: number
  name: string
  path: string
}

// Containers active before a change and not after it.
export function emptiedBy(before: StructureNode[], after: StructureNode[]): Emptied[] {
  const was = activeContainerIds(before)
  const now = activeContainerIds(after)
  const byId = index(after)
  return [...was]
    .filter((id) => !now.has(id))
    .map((id) => {
      const n = byId.get(id)!
      return { id, level: n.level, name: n.name, path: pathOf(byId, n) }
    })
}

export function afterArchive(nodes: StructureNode[], rowIds: Set<string>): StructureNode[] {
  return nodes.map((n) => (rowIds.has(n.id) ? { ...n, archived_at: 'pending', active: false } : n))
}

export function afterMove(nodes: StructureNode[], ids: Set<string>, parentId: string): StructureNode[] {
  return nodes.map((n) => (ids.has(n.id) ? { ...n, parent_id: parentId } : n))
}

// The nodes at or under any of these.
export function subtreeIds(nodes: StructureNode[], rootIds: Iterable<string>): Set<string> {
  const byId = index(nodes)
  const roots = [...rootIds]
  return new Set(nodes.filter((n) => roots.some((r) => isUnder(byId, n, r))).map((n) => n.id))
}

// The boundaries a write removes, as the server counts them
// (routes/structure.js): "Move preserves identity and clears geometry", so
// the moved subtree's go; archiving takes its rows' out of attribution; and
// in both, a container left empty is the container's archive, so its
// boundary goes too.
export function boundariesOnMove(nodes: StructureNode[], boundaries: StructureBoundary[], ids: Set<string>, parentId: string): StructureBoundary[] {
  const clears = subtreeIds(nodes, ids)
  for (const e of emptiedBy(nodes, afterMove(nodes, ids, parentId))) clears.add(e.id)
  return boundaries.filter((b) => clears.has(b.node_id))
}

export function boundariesOnArchive(nodes: StructureNode[], boundaries: StructureBoundary[], rowIds: Set<string>): StructureBoundary[] {
  const clears = new Set(rowIds)
  for (const e of emptiedBy(nodes, afterArchive(nodes, rowIds))) clears.add(e.id)
  return boundaries.filter((b) => clears.has(b.node_id))
}

// "The mapping cell tells the truth about level. At row mapping it reads
// Mapped / Unmapped. Below the mapping level it shows the ancestor's
// coverage ("Block 1 mapped")."
export function mappingCell(nodes: StructureNode[] | Tree, site: Pick<StructureSite, 'map_level'>, boundaries: StructureBoundary[], node: StructureNode): { text: string; mapped: boolean } {
  const { at, mapped } = coverageOf(nodes, site, boundaries, node)
  if (!at || at.id === node.id) return { text: mapped ? 'Mapped' : 'Unmapped', mapped }
  return { text: `${at.name} ${mapped ? 'mapped' : 'unmapped'}`, mapped }
}

// The node at the mapping level that covers this one (itself, or the
// ancestor at that level), and whether it has a boundary.
export function coverageOf(nodes: StructureNode[] | Tree, site: Pick<StructureSite, 'map_level'>, boundaries: StructureBoundary[], node: StructureNode): { at: StructureNode | null; mapped: boolean } {
  const byId = index(nodes)
  let at: StructureNode | undefined = node
  while (at && at.level > site.map_level) at = at.parent_id ? byId.get(at.parent_id) : undefined
  if (!at || at.level !== site.map_level) return { at: null, mapped: false }
  return { at, mapped: boundaries.some((b) => b.node_id === at!.id) }
}

// An area as a grower reads it: square metres for a small one, hectares
// once it reaches one.
export function areaText(m2: number | undefined | null): string {
  const v = Number(m2) || 0
  if (v < 10000) return `${Math.round(v).toLocaleString('en-NZ')} m²`
  return `${(v / 10000).toLocaleString('en-NZ', { maximumFractionDigits: 2 })} ha`
}

// The active rows at or under a node (the node itself when it is a row).
export function activeRowsUnder(nodes: StructureNode[], nodeId: string): StructureNode[] {
  const byId = index(nodes)
  return nodes.filter((n) => isActiveRow(n) && isUnder(byId, n, nodeId))
}

// Would archiving these rows leave the Site with none?
export function leavesNoActiveRow(nodes: StructureNode[], rowIds: Set<string>): boolean {
  return !nodes.some((n) => isActiveRow(n) && !rowIds.has(n.id))
}

// Add many: "Prefix, starting number, arbitrary quantity ... with a live
// preview of the generated names". The server names row i `${prefix}
// ${start + i}` (routes/structure.js); the suggested start follows the
// highest number already used with that prefix under the parent.
export const MAX_ADD = 400

export function generatedNames(prefix: string, start: number, quantity: number): string[] {
  const p = prefix.trim()
  if (!p || !Number.isInteger(start) || start < 0 || !Number.isInteger(quantity) || quantity < 1) return []
  return Array.from({ length: Math.min(quantity, MAX_ADD) }, (_, i) => `${p} ${start + i}`)
}

export function suggestedStart(siblings: StructureNode[], prefix: string): number {
  const p = prefix.trim()
  if (!p) return 1
  let max = 0
  const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`^${esc} (\\d+)$`)
  for (const n of siblings) {
    const m = n.name.match(re)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return max + 1
}

// The preview line: every name when there are few, the ends when many.
export function previewLine(names: string[]): string {
  if (names.length <= 6) return names.join(', ')
  return `${names.slice(0, 3).join(', ')}, … ${names.slice(-2).join(', ')}`
}

// Bulk edit: "Mixed fields read 'Multiple values'".
export function common<T>(values: T[]): { mixed: boolean; value: T | undefined } {
  if (!values.length) return { mixed: false, value: undefined }
  const first = values[0]
  return values.every((v) => v === first) ? { mixed: false, value: first } : { mixed: true, value: undefined }
}

// Bulk edit's variety preview, before Apply: which rows picking locks (left
// unchanged, named), which change, and which of those would stop matching
// their job and be unassigned.
export interface VarietyPlan {
  locked: StructureNode[]
  changing: StructureNode[]
  unassigned: { row: StructureNode; job: StructureJob }[]
}

export function varietyPlan(rows: StructureNode[], jobs: StructureJob[], variety: string): VarietyPlan {
  const v = variety.trim()
  const byJob = new Map(jobs.map((j) => [j.id, j]))
  const locked = rows.filter((r) => r.picking_recorded)
  const changing = rows.filter((r) => !r.picking_recorded && r.exact_variety !== v)
  const unassigned = changing
    .map((row) => ({ row, job: row.picking_job_id ? byJob.get(row.picking_job_id) : undefined }))
    .filter((x): x is { row: StructureNode; job: StructureJob } => !!x.job && x.job.exact_variety !== v)
  return { locked, changing, unassigned }
}

// History (GET /api/sites/:id/structure/history): each audit row read back
// as a sentence and its details. The payloads are the ones
// routes/structure.js, routes/sites.js and routes/picking-jobs.js write.
export interface HistoryEvent {
  id: string
  event_type: string
  payload: any
  created_at: string
  // The person behind the actor's sign-in, else its email; null when the
  // actor has no sign-in in this workspace.
  actor_label?: string | null
  // Packing events: box type and lid names by id, as they are now.
  names?: Record<string, string>
}

// Who made a change: the person, else the raw actor id the event holds.
export const historyActor = (e: HistoryEvent) => e.actor_label || e.payload?.actor || null

const FIELD: Record<string, string> = {
  name: 'Name',
  sort_order: 'Order',
  exact_variety: 'Variety',
  plant_count: 'Plants',
  location_id: 'Location ID',
  grower_pin: 'Grower PIN',
}
const shown = (v: unknown) => (v === null || v === undefined || v === '' ? 'none' : String(v))
// A block with no PIN of its own inherits the Site's.
const nodeValue = (k: string, v: unknown) => (k === 'grower_pin' && (v === null || v === undefined) ? 'inherited' : shown(v))

// Site settings (PUT /api/sites/:id, routes/sites.js), read back.
const SITE_FIELD: Record<string, string> = {
  name: 'Name',
  address: 'Address',
  size_hectares: 'Size',
  timezone: 'Time zone',
  grower_pin: 'Grower PIN',
  estimated_item_weight_g: 'Estimated item weight',
  estimated_bin_weight_g: 'Estimated bin weight',
  level1_label: 'Level 1',
  level2_label: 'Level 2',
  level3_label: 'Level 3',
  show_level2: 'Level 2',
  map_level: 'Mapping level',
  archived: 'Status',
  region: 'Region',
  map_centre: 'Map centre',
  map_zoom: 'Default zoom',
}
function siteValue(k: string, v: unknown): string {
  if (k === 'map_centre') {
    const c = v as { lat?: number; lng?: number } | null | undefined
    return c && c.lat !== undefined && c.lng !== undefined ? `${c.lat}, ${c.lng}` : 'not set'
  }
  if (k === 'map_zoom') return v === null || v === undefined ? 'not set' : `zoom ${v}`
  if (k === 'region') return v ? String(v) : 'not set'
  if (k === 'archived') return v ? 'Archived' : 'Active'
  if (k === 'show_level2') return v === false ? 'hidden' : 'shown'
  if (k === 'map_level') return `level ${v}`
  if (k === 'size_hectares') return v === null || v === undefined ? 'not set' : `${v} ha`
  if (k === 'estimated_item_weight_g') return !v ? 'not set' : `${v} g`
  if (k === 'estimated_bin_weight_g') return !v ? 'not set' : `${Number(v) / 1000} kg`
  if (k === 'grower_pin') return v ? String(v) : 'not set'
  if (k === 'timezone') return v ? String(v) : 'inherits the subscription zone'
  return shown(v)
}
const rows = (n: number) => `${n} ${n === 1 ? 'row' : 'rows'}`

// Picking job events (routes/picking-jobs.js). Each names the job as it was
// then; events from before that fall back to its name now.
const JOB_ORDER = ['name', 'exact_variety', 'colour', 'packed_at', 'marketed_by', 'distributed_by', 'active', 'archived']
const JOB_FIELD: Record<string, string> = {
  name: 'Name',
  exact_variety: 'Variety',
  colour: 'Colour',
  packed_at: 'Packed at',
  marketed_by: 'Marketed by',
  distributed_by: 'Distributed by',
  active: 'Active',
  archived: 'Status',
}
function jobValue(k: string, v: unknown): string {
  if (k === 'active') return v ? 'yes' : 'no'
  if (k === 'archived') return v ? 'Archived' : 'Not archived'
  if (k === 'colour' && v) {
    const name = colourName(String(v))
    return name === 'Pick one' ? String(v) : name
  }
  return v === null || v === undefined || v === '' ? 'not set' : String(v)
}
const jobName = (p: any, jobs: StructureJob[]) => p.job_name || jobs.find((j) => j.id === p.aggregate_id)?.name || ''
const restated = (p: any) =>
  p.restates_reported && p.restatement ? [`Restated ${kg(p.restatement.kg_recorded)} of picking already reported (acknowledged)`] : []

export function historyEntry(e: HistoryEvent, jobs: StructureJob[]): { title: string; lines: string[] } {
  const p = e.payload || {}
  const emptied = (p.emptied || []).map((x: Emptied) => `${x.path} emptied`)
  // A list on a move or archive; on Site settings it is a count.
  const cleared = (Array.isArray(p.boundaries_removed) ? p.boundaries_removed : []).map((x: { path: string; area_m2: number }) => `Boundary removed: ${x.path} (${areaText(x.area_m2)})`)
  const points = (s: any) => `${areaText(s?.area_m2)}, ${(s?.points || []).length} points`
  switch (e.event_type) {
    case 'structure_boundary_drawn':
      return { title: `Boundary drawn for ${p.path}`, lines: [points(p.after)] }
    case 'structure_boundary_reshaped':
      return { title: `Boundary reshaped for ${p.path}`, lines: [`${points(p.before)} → ${points(p.after)}`] }
    case 'structure_boundary_removed':
      return { title: `Boundary removed from ${p.path}`, lines: [`${points(p.before)}. The shape stays on record.`] }
    case 'structure_rows_created': {
      const after = p.after || []
      const made = (p.created_containers || []).map((c: { name: string }) => c.name)
      return {
        title: `Added ${rows(after.length)} under ${p.parent_path}`,
        lines: [
          ...(made.length ? [`New: ${made.join(', ')}`] : []),
          previewLine(after.map((r: { name: string; location_id: string | null }) => (r.location_id ? `${r.name} (${r.location_id})` : r.name))),
        ],
      }
    }
    case 'structure_imported': {
      const after = p.after || []
      const made = (p.created_containers || []).map((c: { path: string }) => c.path)
      const held = p.held_back || []
      const heldTotal = (p.counts?.held || 0) + (p.counts?.boundaries_held || 0)
      const heldLine = (h: { sheet: string; rows: number[]; reasons: string[] }) =>
        `${h.sheet} ${h.rows.length === 1 ? 'row' : 'rows'} ${h.rows.join(', ')}: ${h.reasons.join('; ')}`
      return {
        title: `Imported ${p.summary || rows(after.length)} from ${p.file_name || 'a workbook'}`,
        lines: [
          ...(made.length ? [`New: ${made.join(', ')}`] : []),
          ...(after.length ? [previewLine(after.map((r: { path: string; location_id: string | null }) => (r.location_id ? `${r.path} (${r.location_id})` : r.path)))] : []),
          ...(p.boundaries || []).map((b: { path: string; area_m2: number }) => `Boundary: ${b.path} (${areaText(b.area_m2)})`),
          ...(heldTotal ? [`${heldTotal} held back, nothing written for them`] : []),
          ...held.slice(0, 5).map(heldLine),
          ...(held.length > 5 ? [`and ${held.length - 5} more held back`] : []),
        ],
      }
    }
    case 'structure_node_updated':
      return {
        title: `Edited ${p.path}`,
        lines: [
          ...Object.keys(p.after || {}).map((k) => `${FIELD[k] || k}: ${nodeValue(k, p.before?.[k])} → ${nodeValue(k, p.after[k])}`),
          ...(p.unassigned_from ? [`Unassigned from ${p.unassigned_from.name}`] : []),
        ],
      }
    case 'structure_bulk_edited':
      return {
        title: `Bulk edit: ${(p.changes || []).length} ${(p.changes || []).length === 1 ? 'change' : 'changes'}`,
        lines: [
          ...(p.changes || []).map((c: any) => `${c.path}: ${FIELD[c.field] || c.field} ${shown(c.before)} → ${shown(c.after)}`),
          ...((p.left_unchanged_locked || []).length ? [`Variety left unchanged (picking recorded): ${p.left_unchanged_locked.map((r: any) => r.path).join(', ')}`] : []),
          ...(p.unassigned || []).map((u: any) => `${u.path} unassigned from ${u.job.name}`),
        ],
      }
    case 'structure_moved':
      return {
        title: `Moved ${(p.moved || []).length === 1 ? p.moved[0].name : `${p.moved.length} items`} to ${p.moved?.[0]?.to}`,
        lines: [...(p.moved || []).map((m: any) => `${m.name}: from ${m.from}`), ...emptied, ...cleared],
      }
    case 'structure_reordered':
      return {
        // The level's label as it was then (level1_label at the time).
        title: `Reordered ${p.label ? `${p.label.toLowerCase()}s` : `level ${p.level}`}${p.parent ? ` in ${p.parent.path}` : ''}`,
        lines: [`Was: ${(p.before || []).join(', ')}`, `Now: ${(p.after || []).join(', ')}`],
      }
    case 'structure_rows_archived':
      return {
        title: p.scope ? `Archived every row in ${p.scope.path}` : `Archived ${rows((p.archived || []).length)}`,
        lines: [...(p.archived || []).map((r: any) => (r.location_id ? `${r.path} (${r.location_id})` : r.path)), ...emptied, ...cleared],
      }
    case 'structure_row_replanted':
      return {
        title: `Replanted ${p.path}`,
        lines: [
          `Variety: ${shown(p.before?.exact_variety)} → ${shown(p.after?.exact_variety)}`,
          `Plants: ${shown(p.before?.plant_count)} → ${shown(p.after?.plant_count)}`,
          p.after?.location_id_kept ? `Location ID kept: ${shown(p.after?.location_id)}` : `New Location ID: ${shown(p.after?.location_id)}`,
          ...(p.boundary_kept ? ['Boundary kept: same ground, new planting'] : []),
        ],
      }
    case 'structure_site_created':
      return { title: `Site ${p.name} created`, lines: [] }
    case 'structure_variety_added':
      return { title: `Variety ${p.after?.name} added to the list`, lines: [] }
    case 'structure_variety_archived':
    case 'structure_variety_restored': {
      const carried = `${rows(p.rows || 0)} and ${p.jobs === 1 ? '1 job' : `${p.jobs || 0} jobs`} carry it`
      return e.event_type === 'structure_variety_archived'
        ? { title: `Variety ${p.name} archived`, lines: [`No longer offered for new work; ${carried}, and keep it`] }
        : { title: `Variety ${p.name} restored`, lines: [`Offered for new work again; ${carried}`] }
    }
    case 'structure_site_settings':
      return {
        title: 'Site settings changed',
        lines: [
          ...Object.keys(p.after || {}).map((k) => `${SITE_FIELD[k] || k}: ${siteValue(k, p.before?.[k])} → ${siteValue(k, p.after[k])}`),
          ...(p.boundaries_removed ? [`${p.boundaries_removed} ${p.boundaries_removed === 1 ? 'boundary' : 'boundaries'} removed with the old mapping level`] : []),
        ],
      }
    case 'picking_job_created':
      return {
        title: `Picking job ${jobName(p, jobs)} created`,
        lines: JOB_ORDER.filter((k) => k !== 'name' && k !== 'archived' && p.after?.[k] !== null && p.after?.[k] !== undefined).map(
          (k) => `${JOB_FIELD[k]}: ${jobValue(k, p.after[k])}`
        ),
      }
    case 'picking_job_changed':
      return {
        title: `Picking job ${jobName(p, jobs)} edited`,
        lines: JOB_ORDER.filter((k) => p.after && k in p.after).map((k) => `${JOB_FIELD[k]}: ${jobValue(k, p.before?.[k])} → ${jobValue(k, p.after[k])}`),
      }
    case 'picking_job_packing_changed': {
      const names = e.names || {}
      const named = (id: unknown, none: string) => (id ? names[String(id)] || 'a removed item' : none)
      const side = (s: any) => ({
        box: named(s?.box_type_id, 'no box type'),
        pallet: s?.boxes_per_pallet ? `${s.boxes_per_pallet} per pallet` : 'not set',
        lid: named(s?.lid_id, 'no lid'),
      })
      const before = side(p.before)
      const after = side(p.after)
      return {
        title: `Picking job ${jobName(p, jobs)} packing ${p.before ? 'changed' : 'set'}`,
        lines: [
          ...(before.box !== after.box ? [`Box type: ${p.before ? `${before.box} → ` : ''}${after.box}`] : []),
          ...(before.pallet !== after.pallet ? [`Boxes per pallet: ${p.before ? `${before.pallet} → ` : ''}${after.pallet}`] : []),
          ...(before.lid !== after.lid ? [`Lid: ${p.before ? `${before.lid} → ` : ''}${after.lid}`] : []),
        ],
      }
    }
    case 'picking_job_rate_created':
      return {
        title: `Picking job ${jobName(p, jobs)}: rate added`,
        lines: [`${perKg(p.after?.rate_per_kg)}, ${period(p.after?.effective_from, p.after?.effective_to ?? null)}`, ...restated(p)],
      }
    case 'picking_job_rate_amended': {
      const b = p.before || {}
      const a = p.after || {}
      return {
        title: `Picking job ${jobName(p, jobs)}: rate edited`,
        lines: [
          ...(b.rate_per_kg !== a.rate_per_kg ? [`Rate: ${perKg(b.rate_per_kg)} → ${perKg(a.rate_per_kg)}`] : []),
          `Period: ${period(b.effective_from, b.effective_to ?? null)}${b.effective_from !== a.effective_from || b.effective_to !== a.effective_to ? ` → ${period(a.effective_from, a.effective_to ?? null)}` : ''}`,
          ...restated(p),
        ],
      }
    }
    case 'picking_job_rate_removed':
      return {
        title: `Picking job ${jobName(p, jobs)}: rate removed`,
        lines: [
          `${perKg(p.before?.rate_per_kg)}, ${period(p.before?.effective_from, p.before?.effective_to ?? null)}`,
          p.takes_over ? `${perKg(p.takes_over.rate_per_kg)} covers that period now` : 'No rate covers that period now',
          ...restated(p),
        ],
      }
    case 'picking_job_members_changed': {
      return {
        title: `Picking job ${jobName(p, jobs)} membership changed`.replace('  ', ' '),
        lines: [
          ...(p.added || []).map((a: any) => `Added ${a.name}${a.from_job ? ` (from ${a.from_job.name})` : ''}`),
          ...(p.removed || []).map((r: any) => `Removed ${r.name}`),
        ],
      }
    }
    default:
      return { title: e.event_type, lines: [] }
  }
}

export const kg = (n: number | undefined) => `${(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 2 })} kg`
